import { useEffect, useMemo, useRef, useState } from "react";
import { feature } from "topojson-client";
import { geoNaturalEarth1, geoContains } from "d3-geo";
import land from "world-atlas/land-110m.json";
import { useT } from "@/i18n/LocaleContext";

/* GlobalFootprint — quiet editorial world map with N gold markers.
   Renders the world land layer as a single fill using d3-geo, then
   draws bezier arcs from the `origin` marker to every other marker
   with a staggered stroke-dashoffset animation.

   Configurable via props so multiple pages can render distinct
   footprint contexts (Home global list vs Contact operating map).

   Props:
     locations        — array of { id, code, city, lat, lng, labelDx, labelDy }.
                        `code` is the i18n key inside `countryNamespace`.
                        `id` is used for testids and origin selection.
     originId         — the marker id that becomes the gold-ring origin
                        and the source of every arc. Default: "lu".
     eyebrowKey       — i18n key rendered as the small uppercase eyebrow
     headingKey       — i18n key for the H2 title
     bodyKey          — i18n key for the intro paragraph
     mapAltKey        — i18n key for the SVG's role="img" aria-label
     captionKey       — i18n key for the caption below the map
     countryNamespace — i18n key prefix for country name lookup
                        (e.g. "home.footprint_countries" or
                        "contact_map.countries")
     testIdPrefix     — prefix for data-testid attributes on the section,
                        map, markers and arcs. Default: "home-footprint" */

const VIEW_W = 1000;
const VIEW_H = 460;

/* Precomputed digital dot-matrix world map.
   Sample every 2° of lng × 2° of lat between 85°N and 60°S; keep only
   points inside a land polygon (via d3-geo.geoContains). The projection
   is shared with the map component (same fitSize dims), so the projected
   coordinates align exactly with markers and arcs.

   Computed once at module load and reused across both map instances. */
const DOT_STEP = 2;      // degrees
const DOT_LAT_MIN = -60; // skip Antarctica
const DOT_LAT_MAX = 85;

const DOT_GRID = (() => {
  const projection = geoNaturalEarth1().fitSize([VIEW_W, VIEW_H], { type: "Sphere" });
  const landFeature = feature(land, land.objects.land);
  const dots = [];
  for (let lng = -180; lng <= 180; lng += DOT_STEP) {
    for (let lat = DOT_LAT_MIN; lat <= DOT_LAT_MAX; lat += DOT_STEP) {
      if (geoContains(landFeature, [lng, lat])) {
        const p = projection([lng, lat]);
        if (p && Number.isFinite(p[0]) && Number.isFinite(p[1])) {
          dots.push([Math.round(p[0] * 10) / 10, Math.round(p[1] * 10) / 10]);
        }
      }
    }
  }
  return dots;
})();

const HOME_LOCATIONS = [
  { id: "us", code: "united_states", lng: -74.006,  lat: 40.7128, city: "New York",       labelDy: 20 },
  { id: "lu", code: "luxembourg",    lng:   6.1319, lat: 49.6116, city: "Luxembourg City", labelDy: -18 },
  { id: "fr", code: "france",        lng:   2.3522, lat: 48.8566, city: "Paris",          labelDy: 22, labelDx: -32 },
  { id: "de", code: "germany",       lng:   8.6821, lat: 50.1109, city: "Frankfurt",      labelDy: -18, labelDx: 34 },
  { id: "cn", code: "china",         lng: 121.4737, lat: 31.2304, city: "Shanghai",       labelDy: 22 },
  { id: "qa", code: "qatar",         lng:  51.531,  lat: 25.2854, city: "Doha",           labelDy: 22, labelDx: -22 },
  { id: "ae", code: "uae",           lng:  55.2708, lat: 25.2048, city: "Dubai",          labelDy: -18, labelDx: 26 },
];

const arcPath = ([x1, y1], [x2, y2]) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const dr = Math.hypot(dx, dy) || 1;
  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;
  const curve = Math.min(90, dr * 0.32);
  const nx = -dy / dr;
  const ny = dx / dr;
  const sign = ny <= 0 ? 1 : -1;
  const cx = midX + nx * curve * sign;
  const cy = midY + ny * curve * sign;
  return `M ${x1.toFixed(2)},${y1.toFixed(2)} Q ${cx.toFixed(2)},${cy.toFixed(2)} ${x2.toFixed(2)},${y2.toFixed(2)}`;
};

export const GlobalFootprint = ({
  locations = HOME_LOCATIONS,
  originId = "lu",
  eyebrowKey = "home.footprint_eyebrow",
  headingKey = "home.footprint_h2",
  bodyKey = "home.footprint_body",
  mapAltKey = "home.footprint_map_alt",
  captionKey = "home.footprint_caption",
  countryNamespace = "home.footprint_countries",
  testIdPrefix = "home-footprint",
}) => {
  const { t } = useT();
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState(null);
  const rootRef = useRef(null);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || !("IntersectionObserver" in window)) { setVisible(true); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setVisible(true); io.unobserve(e.target); } });
    }, { threshold: 0.18 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const { points, origin } = useMemo(() => {
    const projection = geoNaturalEarth1().fitSize([VIEW_W, VIEW_H], { type: "Sphere" });
    const projected = locations.map((p) => ({ ...p, xy: projection([p.lng, p.lat]) }));
    const origin = projected.find((p) => p.id === originId) || projected[0];
    return { points: projected, origin };
  }, [locations, originId]);

  const others = points.filter((p) => p.id !== origin.id);

  return (
    <section ref={rootRef} data-testid={testIdPrefix} className="bg-cream">
      <div className="mx-auto max-w-[1360px] px-6 md:px-10 lg:px-14 py-32 md:py-40">
        <div className="max-w-3xl cascade">
          <div className="reveal rule-gold w-16" aria-hidden />
          <div className="reveal mt-8 flex items-center gap-4">
            <span
              className="eyebrow font-sans text-[11px] font-medium uppercase text-navy/60"
              style={{ letterSpacing: "0.28em" }}
            >
              {t(eyebrowKey)}
            </span>
          </div>
          <h2 className="reveal mt-8 md:mt-10 font-serif font-normal text-navy text-[30px] md:text-[42px] leading-[1.1]">
            {t(headingKey)}
          </h2>
          <p className="reveal mt-6 md:mt-8 font-serif font-normal text-navy/85 text-[18px] md:text-[20px] leading-[1.7] max-w-xl">
            {t(bodyKey)}
          </p>
        </div>

        <div className="mt-20 md:mt-28 reveal">
          <div className="relative">
            <svg
              viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
              preserveAspectRatio="xMidYMid meet"
              width="100%"
              className="block fp-globe"
              role="img"
              aria-label={t(mapAltKey)}
              data-testid={`${testIdPrefix}-map`}
            >
              {/* Digital dot-matrix landmass. Rendered as a single <path>
                  of tiny circles via `M x y m 0 0 a r 0 0 0 (r*2) 0 a r 0 0 0 -(r*2) 0`
                  is heavier than plain <circle> at 3k dots, so we keep plain
                  circles for readability. Precomputed DOT_GRID (module-scope). */}
              <g data-testid={`${testIdPrefix}-dots`} className="fp-dots">
                {DOT_GRID.map(([x, y], i) => (
                  <circle key={i} cx={x} cy={y} r="1.2" />
                ))}
              </g>

              <g>
                {origin && others.map((p, i) => (
                  <path
                    key={p.id}
                    id={`${testIdPrefix}-path-${p.id}`}
                    d={arcPath(origin.xy, p.xy)}
                    fill="none"
                    stroke="#B8A26A"
                    strokeOpacity={active === p.id || active === origin.id ? 0.85 : 0.55}
                    strokeWidth={active === p.id ? 1.4 : 1}
                    strokeLinecap="round"
                    pathLength="1"
                    strokeDasharray="1"
                    data-testid={`${testIdPrefix}-arc-${p.id}`}
                    style={{
                      strokeDashoffset: visible ? 0 : 1,
                      transition: [
                        `stroke-dashoffset 1800ms cubic-bezier(0.22, 1, 0.36, 1) ${420 + i * 180}ms`,
                        "stroke-opacity 700ms ease-out",
                        "stroke-width 700ms ease-out",
                      ].join(", "),
                    }}
                  />
                ))}
              </g>

              {/* Phase 9 — light particles travelling along each arc via
                  CSS offset-path. animateMotion + mpath does not always
                  render reliably through React JSX; offset-path with a
                  CSS animation is a modern browser-native alternative. */}
              {visible && origin && (
                <g data-testid={`${testIdPrefix}-particles`}>
                  {others.map((p, i) => {
                    const d = arcPath(origin.xy, p.xy);
                    const dur = `${9 + (i % 3) * 1.5}s`;
                    const delay = `${0.4 + i * 1.5}s`;
                    return (
                      <circle
                        key={p.id}
                        r="2.5"
                        cx="0"
                        cy="0"
                        className="fp-particle-anim"
                        data-testid={`${testIdPrefix}-particle-${p.id}`}
                        style={{
                          offsetPath: `path("${d}")`,
                          WebkitOffsetPath: `path("${d}")`,
                          animationDuration: dur,
                          animationDelay: delay,
                        }}
                      />
                    );
                  })}
                </g>
              )}

              <g>
                {points.map((p, i) => {
                  const isActive = active === p.id;
                  const isOrigin = p.id === origin.id;
                  const dx = p.labelDx || 0;
                  const dy = p.labelDy || 20;
                  return (
                    <g
                      key={p.id}
                      transform={`translate(${p.xy[0].toFixed(2)}, ${p.xy[1].toFixed(2)})`}
                      onMouseEnter={() => setActive(p.id)}
                      onMouseLeave={() => setActive((cur) => (cur === p.id ? null : cur))}
                      onFocus={() => setActive(p.id)}
                      onBlur={() => setActive((cur) => (cur === p.id ? null : cur))}
                      tabIndex={0}
                      role="button"
                      aria-label={`${t(`${countryNamespace}.${p.code}`)} — ${p.city}`}
                      data-testid={`${testIdPrefix}-marker-${p.id}`}
                      className="cursor-default outline-none"
                      style={{
                        opacity: visible ? 1 : 0,
                        transition: `opacity 900ms ease-out ${900 + i * 120}ms`,
                      }}
                    >
                      <circle
                        r={isOrigin ? 5 : 4}
                        fill="#B8A26A"
                        fillOpacity="0.35"
                        className="footprint-pulse"
                        style={{ animationDelay: `${i * 400}ms` }}
                      />
                      {isOrigin && (
                        <circle r={9} fill="none" stroke="#B8A26A" strokeOpacity="0.55" strokeWidth="1" />
                      )}
                      <circle
                        r={isOrigin ? 3.6 : 2.8}
                        fill="#B8A26A"
                        stroke="#F7F8FA"
                        strokeWidth="1"
                      />
                      <g
                        transform={`translate(${dx}, ${dy})`}
                        style={{
                          opacity: isActive ? 1 : 0,
                          transition: "opacity 500ms ease-out",
                          pointerEvents: "none",
                        }}
                      >
                        <rect
                          x={-58}
                          y={dy < 0 ? -22 : 2}
                          width="116"
                          height="34"
                          fill="#F7F8FA"
                          fillOpacity="0.94"
                        />
                        <text
                          textAnchor="middle"
                          y={dy < 0 ? -10 : 14}
                          fill="#0B132B"
                          fontFamily="Inter, sans-serif"
                          fontSize="9.5"
                          fontWeight="500"
                          style={{ letterSpacing: "0.18em", textTransform: "uppercase" }}
                        >
                          {t(`${countryNamespace}.${p.code}`)}
                        </text>
                        <text
                          textAnchor="middle"
                          y={dy < 0 ? 4 : 28}
                          fill="#0B132B"
                          fillOpacity="0.7"
                          fontFamily="Cormorant Garamond, Georgia, serif"
                          fontSize="13"
                          fontStyle="italic"
                        >
                          {p.city}
                        </text>
                      </g>
                    </g>
                  );
                })}
              </g>
            </svg>
          </div>

          <div className="mt-14 md:mt-16 flex items-center gap-4">
            <span className="h-px w-10 bg-gold" aria-hidden />
            <span
              className="eyebrow font-sans text-[10.5px] font-medium uppercase text-navy/55"
              style={{ letterSpacing: "0.28em" }}
            >
              {t(captionKey)}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

/* Location set for the Contact page — 6 arcs from Luxembourg. */
export const CONTACT_LOCATIONS = [
  { id: "lu", code: "luxembourg",  lng:   6.1319, lat: 49.6116, city: "Luxembourg City", labelDy: -18 },
  { id: "de", code: "germany",     lng:   8.6821, lat: 50.1109, city: "Frankfurt",       labelDy: -18, labelDx: 44 },
  { id: "ch", code: "switzerland", lng:   8.5417, lat: 47.3769, city: "Zurich",          labelDy: 22,  labelDx: 22 },
  { id: "fr", code: "france",      lng:   2.3522, lat: 48.8566, city: "Paris",           labelDy: 22,  labelDx: -34 },
  { id: "qa", code: "qatar",       lng:  51.531,  lat: 25.2854, city: "Doha",            labelDy: 22,  labelDx: -24 },
  { id: "ae", code: "uae",         lng:  55.2708, lat: 25.2048, city: "Dubai",           labelDy: -18, labelDx: 26 },
  { id: "cn", code: "china",       lng: 121.4737, lat: 31.2304, city: "Shanghai",        labelDy: 22 },
];

export default GlobalFootprint;
